/**
 * PressPage component for Agent Nexus
 * 
 * This component displays press releases, media coverage, and press resources.
 */

import React from 'react';
import '../components/IconStyles.css';

const PressPage = () => {
  // Press releases data
  const pressReleases = [
    {
      id: 1,
      title: 'Agent Nexus Raises $12M Series A to Expand AI Automation Platform',
      date: 'June 15, 2023',
      excerpt: 'Funding will accelerate product development and international expansion as demand for AI automation solutions grows.',
      link: '/press/releases/series-a-funding'
    },
    {
      id: 2,
      title: 'Agent Nexus Launches Enterprise Platform with Advanced Security Features',
      date: 'March 8, 2023',
      excerpt: 'New enterprise-grade platform offers enhanced security, compliance, and scalability for large organizations.',
      link: '/press/releases/enterprise-platform-launch'
    },
    {
      id: 3,
      title: 'Agent Nexus Partners with Leading CRM Provider to Enhance Customer Service Automation',
      date: 'January 22, 2023',
      excerpt: 'Strategic partnership will integrate Agent Nexus AI capabilities with popular CRM platform, benefiting thousands of businesses.',
      link: '/press/releases/crm-partnership'
    },
    {
      id: 4,
      title: 'Agent Nexus Named to "AI Companies to Watch" List for 2023',
      date: 'December 10, 2022',
      excerpt: 'Industry recognition highlights Agent Nexus\'s innovative approach to business process automation.',
      link: '/press/releases/ai-companies-to-watch'
    },
    {
      id: 5,
      title: 'Agent Nexus Expands Leadership Team with Key Executive Hires',
      date: 'October 5, 2022',
      excerpt: 'Company adds experienced executives in product, marketing, and sales roles to support rapid growth.',
      link: '/press/releases/executive-hires'
    }
  ];

  // Media coverage data
  const mediaCoverage = [
    {
      id: 1,
      title: 'How Agent Nexus is Transforming Business Operations with AI',
      publication: 'Tech Innovators',
      date: 'May 28, 2023',
      excerpt: 'An in-depth look at how Agent Nexus is helping businesses automate complex workflows and improve efficiency.',
      link: '/press/coverage/tech-innovators-transformation',
      logo: '/images/press/tech-innovators.svg'
    },
    {
      id: 2,
      title: 'The Future of Work: AI Agents and Human Collaboration',
      publication: 'Business Weekly',
      date: 'April 15, 2023',
      excerpt: 'Agent Nexus CEO Sarah Johnson discusses how AI and humans can work together to achieve better results.',
      link: '/press/coverage/business-weekly-future-of-work',
      logo: '/images/press/business-weekly.svg'
    },
    {
      id: 3,
      title: '10 AI Startups Reshaping Enterprise Software',
      publication: 'Enterprise Tech Today',
      date: 'March 3, 2023',
      excerpt: 'Agent Nexus featured as a leading innovator in the enterprise AI space, recognized for its unique approach to automation.',
      link: '/press/coverage/enterprise-tech-startups',
      logo: '/images/press/enterprise-tech.svg'
    },
    {
      id: 4,
      title: 'Case Study: How Company X Increased Efficiency by 45% with Agent Nexus',
      publication: 'AI Business',
      date: 'February 12, 2023',
      excerpt: 'A detailed look at how a major corporation implemented Agent Nexus to transform their customer service operations.',
      link: '/press/coverage/ai-business-case-study',
      logo: '/images/press/ai-business.svg'
    }
  ];

  // Handle image loading errors
  const handleImageError = (e) => {
    e.target.src = '/images/general/placeholder.svg';
  };

  return (
    <div className="press-page">
      {/* Hero Section */}
      <section className="press-hero">
        <div className="container">
          <h1>Press & Media</h1>
          <p>Latest news, press releases, and media resources for Agent Nexus</p>
        </div>
      </section>

      {/* Press Contact */}
      <section className="press-contact">
        <div className="container">
          <div className="contact-card">
            <div className="contact-info">
              <h2>Press Contact</h2>
              <p>For press inquiries, please contact our media relations team:</p>
              <div className="contact-details">
                <p><strong>Email:</strong> <a href="mailto:press@agentnexus.com">press@agentnexus.com</a></p>
                <p><strong>Phone:</strong> (555) 123-4567</p>
              </div>
            </div>
            <div className="contact-cta">
              <a href="/contact" className="press-btn">Contact Us</a>
              <a href="#press-kit" className="press-btn-secondary">Download Press Kit</a>
            </div>
          </div>
        </div>
      </section>

      {/* Press Releases */}
      <section className="press-releases">
        <div className="container">
          <h2>Press Releases</h2>
          <div className="releases-list">
            {pressReleases.map(release => (
              <div className="release-item" key={release.id}>
                <div className="release-date">{release.date}</div>
                <h3 className="release-title">
                  <a href={release.link}>{release.title}</a>
                </h3>
                <p className="release-excerpt">{release.excerpt}</p>
                <a href={release.link} className="read-more">Read Full Release</a>
              </div>
            ))}
          </div>
          <div className="view-all">
            <a href="/press/all-releases" className="view-all-btn">View All Press Releases</a>
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      <section className="media-coverage">
        <div className="container">
          <h2>Media Coverage</h2>
          <div className="coverage-grid">
            {mediaCoverage.map(item => (
              <div className="coverage-card" key={item.id}>
                <div className="publication-logo">
                  <img 
                    src={item.logo} 
                    alt={item.publication} 
                    onError={handleImageError}
                  />
                </div>
                <div className="coverage-content">
                  <div className="coverage-meta">
                    <span className="publication-name">{item.publication}</span>
                    <span className="coverage-date">{item.date}</span>
                  </div>
                  <h3 className="coverage-title">
                    <a href={item.link}>{item.title}</a>
                  </h3>
                  <p className="coverage-excerpt">{item.excerpt}</p>
                  <a href={item.link} className="read-article">Read Article</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Press Kit */}
      <section className="press-kit" id="press-kit">
        <div className="container">
          <h2>Press Kit</h2>
          <p className="kit-intro">
            Download official Agent Nexus logos, executive headshots, product screenshots, and brand guidelines.
          </p>
          <div className="kit-grid">
            <div className="kit-card">
              <div className="kit-icon">
                <svg className="icon">
                  <use xlinkHref="/images/general/icons.svg#icon-images"></use>
                </svg>
              </div>
              <h3>Logos & Brand Assets</h3>
              <p>Official Agent Nexus logos in various formats and brand color guidelines.</p>
              <a href="/press/downloads/logos.zip" className="download-btn">Download (ZIP, 15MB)</a>
            </div>
            <div className="kit-card">
              <div className="kit-icon">
                <svg className="icon">
                  <use xlinkHref="/images/general/icons.svg#icon-user-tie"></use>
                </svg>
              </div>
              <h3>Executive Photos</h3>
              <p>High-resolution headshots of Agent Nexus leadership team.</p>
              <a href="/press/downloads/executive-photos.zip" className="download-btn">Download (ZIP, 22MB)</a>
            </div>
            <div className="kit-card">
              <div className="kit-icon">
                <svg className="icon">
                  <use xlinkHref="/images/general/icons.svg#icon-desktop"></use>
                </svg>
              </div>
              <h3>Product Screenshots</h3>
              <p>Screenshots of the Agent Nexus platform and key features.</p>
              <a href="/press/downloads/screenshots.zip" className="download-btn">Download (ZIP, 18MB)</a>
            </div>
            <div className="kit-card">
              <div className="kit-icon">
                <svg className="icon">
                  <use xlinkHref="/images/general/icons.svg#icon-file-alt"></use>
                </svg>
              </div>
              <h3>Company Fact Sheet</h3>
              <p>Key information about Agent Nexus, our mission, and achievements.</p>
              <a href="/press/downloads/fact-sheet.pdf" className="download-btn">Download (PDF, 2MB)</a>
            </div>
          </div>
        </div>
      </section>

      {/* In The News */}
      <section className="in-the-news">
        <div className="container">
          <h2>Agent Nexus In The News</h2>
          <div className="news-logos">
            <div className="news-logo">
              <img 
                src="/images/press/techcrunch.svg" 
                alt="TechCrunch" 
                onError={handleImageError}
              />
            </div>
            <div className="news-logo">
              <img 
                src="/images/press/forbes.svg" 
                alt="Forbes" 
                onError={handleImageError}
              />
            </div>
            <div className="news-logo">
              <img 
                src="/images/press/wired.svg" 
                alt="Wired" 
                onError={handleImageError}
              />
            </div>
            <div className="news-logo">
              <img 
                src="/images/press/bloomberg.svg" 
                alt="Bloomberg" 
                onError={handleImageError}
              />
            </div>
            <div className="news-logo">
              <img 
                src="/images/press/wsj.svg" 
                alt="Wall Street Journal" 
                onError={handleImageError}
              />
            </div>
            <div className="news-logo">
              <img 
                src="/images/press/cnbc.svg" 
                alt="CNBC" 
                onError={handleImageError}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressPage; 